import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Container, Form, Button, Spinner, Alert, Card, Row, Col } from 'react-bootstrap';
import ReactQuill from 'react-quill';

import VereinbarungService from '../services/vereinbarung.service';
import UserService from '../services/user.service';
import PlatzhalterAuswahlModal from './PlatzhalterAuswahlModal';

const VereinbarungErstellen = () => {
    // === ANFANG: NEUE ZUSTANDSVARIABLEN HINZUGEFÜGT ===
    const [titelId, setTitelId] = useState('');
    const [individuellerTitel, setIndividuellerTitel] = useState('');
    const [partnerId, setPartnerId] = useState('');
    const [inhalt, setInhalt] = useState('');
    const [bearbeiterVon, setBearbeiterVon] = useState('');
    const [bearbeiterAn, setBearbeiterAn] = useState('');
    const [zusammenfassung, setZusammenfassung] = useState('');
    const [anlagen, setAnlagen] = useState('');
    const [gueltigkeitVon, setGueltigkeitVon] = useState('');
    const [gueltigkeitBis, setGueltigkeitBis] = useState('');
    const [kuendigungsfristWert, setKuendigungsfristWert] = useState('');
    const [kuendigungsfristEinheit, setKuendigungsfristEinheit] = useState('Tag(e)');
    // === ENDE: NEUE ZUSTANDSVARIABLEN HINZUGEFÜGT ===

    const [titelList, setTitelList] = useState([]);
    const [titelKatalogList, setTitelKatalogList] = useState([]);
    const [partnerList, setPartnerList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [modalShow, setModalShow] = useState(false);
    const quillRef = useRef(null);
    const navigate = useNavigate();
    // Die Systemvorlage wird in der Uebersicht (S8MyAgreements) gewaehlt und per
    // URL-Parameter uebergeben. Ohne Vorlage zurueck zur Uebersicht.
    const [searchParams] = useSearchParams();
    const templateId = searchParams.get('template');

    // Aktuelle Stufe bestimmt, welche Partner als Empfaenger zur Auswahl stehen
    // (Folgeprompt 16): Die Vertriebskette ist MAINVO -> SubVO -> ADM. Jede Stufe
    // waehlt die unmittelbar darunterliegende Stufe als Vertragspartner.
    //   - MAINVO  -> seine SubVOs   (GET /subvos)
    //   - sonst   -> seine ADMs     (POST /adm/list)  [bisheriges, funktionierendes Verhalten]
    const { user: currentUser } = useSelector((state) => state.auth);
    const isMainVo = Array.isArray(currentUser?.roles) && currentUser.roles.includes('ROLE_MAINVO');

    useEffect(() => {
        if (!templateId) {
            return;
        }
        setLoading(true);
        // Stufenabhaengige Partnerquelle (siehe Hinweis oben).
        const partnerRequest = isMainVo
            ? UserService.getSubVosBoard()
            : UserService.getAllAds({ page: 1, search: '', sort: 'company_name', order: 'asc' });
        Promise.all([
            VereinbarungService.getAllAgreementTitles(),
            partnerRequest,
            VereinbarungService.getTitleCatalog('system').catch(() => ({ data: [] })),
            VereinbarungService.getTitleCatalog('stage').catch(() => ({ data: [] }))
        ])
        .then(([titelResponse, partnerResponse, systemKatalogResponse, stageKatalogResponse]) => {
            setTitelList(titelResponse.data);

            // Partnerliste je nach Quelle vereinheitlichen auf { key, userId, label }.
            // Nur Eintraege mit verknuepftem Benutzerkonto sind als Empfaenger waehlbar.
            // Anzeige: zusammengesetzt aus Firmenname und Ansprechpartner (Vor-/Nachname).
            const buildLabel = (p) => {
                const company = (p.company_name || '').trim();
                const person = [p.contact_first_name, p.contact_last_name]
                    .filter((x) => x && String(x).trim())
                    .join(' ')
                    .trim();
                if (company && person) return `${company} – ${person}`;
                return company || person || '(ohne Bezeichnung)';
            };
            let normalizedPartners = [];
            if (isMainVo) {
                const subList = Array.isArray(partnerResponse.data) ? partnerResponse.data : [];
                normalizedPartners = subList
                    .filter((p) => p && p.user_account && p.user_account.id)
                    .map((p) => ({
                        key: `subvo-${p.sub_vo_id}`,
                        userId: p.user_account.id,
                        label: buildLabel(p),
                    }));
            } else {
                const adList = (partnerResponse.data && partnerResponse.data.ads) || [];
                normalizedPartners = adList
                    .filter((p) => p && p.user_account && p.user_account.id)
                    .map((p) => ({
                        key: `adm-${p.adm_id}`,
                        userId: p.user_account.id,
                        label: buildLabel(p),
                    }));
            }
            setPartnerList(normalizedPartners);

            // System- und eigene Stufen-Titel zusammenfuehren (eindeutig nach Bezeichnung).
            const sysList = Array.isArray(systemKatalogResponse.data) ? systemKatalogResponse.data : [];
            const stageList = Array.isArray(stageKatalogResponse.data) ? stageKatalogResponse.data : [];
            const seen = new Set();
            const merged = [];
            [...sysList, ...stageList].forEach((k) => {
                const key = (k.bezeichnung || '').trim().toLowerCase();
                if (key && !seen.has(key)) { seen.add(key); merged.push(k); }
            });
            setTitelKatalogList(merged);

            // Gewaehlte Systemvorlage anwenden (Inhalt + Standard-Vorbelegung).
            if (templateId) {
                const tpl = (titelResponse.data || []).find(t => String(t.titel_id) === String(templateId));
                if (tpl) {
                    setTitelId(String(templateId));
                    // Vom System vorgegebenen Titel vorbelegen (individuell ueberschreibbar).
                    setIndividuellerTitel(tpl.titel || '');
                    const defaults = parseVorlageDefaults(tpl.vorlage_defaults);
                    setInhalt(tpl.inhalt_vorlage || '');
                    setZusammenfassung(defaults.kurze_zusammenfassung || '');
                    setBearbeiterVon(defaults.bearbeiter_von || '');
                    setBearbeiterAn(defaults.bearbeiter_an || '');
                    setAnlagen(defaults.anlagen || '');
                    setGueltigkeitVon(normDate(defaults.gueltigkeit_von));
                    setGueltigkeitBis(normDate(defaults.gueltigkeit_bis));
                    setKuendigungsfristWert(defaults.kuendigungsfrist_wert != null ? String(defaults.kuendigungsfrist_wert) : '');
                    setKuendigungsfristEinheit(defaults.kuendigungsfrist_einheit || 'Tag(e)');
                }
            }
            setLoading(false);
        })
        .catch(err => {
            setError("Fehler beim Laden der Formulardaten.");
            setLoading(false);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Ohne gewaehlte Systemvorlage zurueck zur Uebersicht (Auswahl erfolgt dort).
    useEffect(() => {
        if (!templateId) {
            navigate('/agreements', { replace: true });
        }
    }, [templateId, navigate]);
    
    const handleSelectPlaceholder = (placeholderKey) => {
        const editor = quillRef.current.getEditor();
        const range = editor.getSelection(true);
        editor.insertText(range.index, placeholderKey, 'user');
    };

    // vorlage_defaults kann je nach DB/Sequelize als Objekt oder JSON-String kommen.
    const parseVorlageDefaults = (raw) => {
        if (!raw) return {};
        if (typeof raw === 'object') return raw;
        try {
            const parsed = JSON.parse(raw);
            return parsed && typeof parsed === 'object' ? parsed : {};
        } catch (e) {
            return {};
        }
    };

    // Datumswerte fuer <input type="date"> auf das Format YYYY-MM-DD bringen.
    const normDate = (value) => {
        if (!value) return '';
        const text = String(value);
        return text.length >= 10 ? text.substring(0, 10) : text;
    };

    // Hinweis: Die Systemvorlage wird in der Uebersicht (S8MyAgreements) gewaehlt
    // und beim Laden des Formulars angewendet (siehe useEffect oben).
    // eslint-disable-next-line no-unused-vars
    const handleTitelChange = (e) => {
        const neueTitelId = e.target.value;
        setTitelId(neueTitelId);

        if (!neueTitelId) {
            return;
        }

        const titel = titelList.find(t => String(t.titel_id) === String(neueTitelId));
        if (!titel) {
            return;
        }

        const defaults = parseVorlageDefaults(titel.vorlage_defaults);
        const vorlage = {
            inhalt: titel.inhalt_vorlage || '',
            zusammenfassung: defaults.kurze_zusammenfassung || '',
            bearbeiterVon: defaults.bearbeiter_von || '',
            bearbeiterAn: defaults.bearbeiter_an || '',
            anlagen: defaults.anlagen || '',
            gueltigkeitVon: normDate(defaults.gueltigkeit_von),
            gueltigkeitBis: normDate(defaults.gueltigkeit_bis),
            kuendigungsfristWert: (defaults.kuendigungsfrist_wert !== null && defaults.kuendigungsfrist_wert !== undefined)
                ? String(defaults.kuendigungsfrist_wert)
                : '',
            kuendigungsfristEinheit: defaults.kuendigungsfrist_einheit || ''
        };

        const istGesetzt = (v) => v !== '' && v !== null && v !== undefined;

        // Gibt es ueberhaupt etwas zu uebernehmen?
        const hatVorlagewerte = Object.values(vorlage).some(istGesetzt);
        if (!hatVorlagewerte) {
            return;
        }

        // Aktuelle Nutzereingaben (Einheit-Default 'Tag(e)' zaehlt nicht als Eingabe).
        const aktuelleEingaben = [
            inhalt, zusammenfassung, bearbeiterVon, bearbeiterAn, anlagen,
            gueltigkeitVon, gueltigkeitBis, kuendigungsfristWert,
            kuendigungsfristEinheit === 'Tag(e)' ? '' : kuendigungsfristEinheit
        ];
        const hatEingaben = aktuelleEingaben.some(istGesetzt);

        const ueberschreiben = !hatEingaben
            ? true
            : window.confirm(
                'Dieser Titel enthält Vorlagewerte. Möchten Sie Ihre bereits eingegebenen Felder mit den Vorlagewerten überschreiben?\n\n' +
                'OK = alle Felder überschreiben\nAbbrechen = nur leere Felder befüllen'
            );

        const setzeFeld = (vorlageWert, aktuellWert, setter) => {
            if (!istGesetzt(vorlageWert)) {
                return;
            }
            if (ueberschreiben || !istGesetzt(aktuellWert)) {
                setter(vorlageWert);
            }
        };

        setzeFeld(vorlage.inhalt, inhalt, setInhalt);
        setzeFeld(vorlage.zusammenfassung, zusammenfassung, setZusammenfassung);
        setzeFeld(vorlage.bearbeiterVon, bearbeiterVon, setBearbeiterVon);
        setzeFeld(vorlage.bearbeiterAn, bearbeiterAn, setBearbeiterAn);
        setzeFeld(vorlage.anlagen, anlagen, setAnlagen);
        setzeFeld(vorlage.gueltigkeitVon, gueltigkeitVon, setGueltigkeitVon);
        setzeFeld(vorlage.gueltigkeitBis, gueltigkeitBis, setGueltigkeitBis);
        setzeFeld(vorlage.kuendigungsfristWert, kuendigungsfristWert, setKuendigungsfristWert);

        // Einheit nur uebernehmen, wenn die Vorlage eine enthaelt; der Standard
        // 'Tag(e)' gilt als "leer" und darf daher ueberschrieben werden.
        if (istGesetzt(vorlage.kuendigungsfristEinheit)) {
            if (ueberschreiben || kuendigungsfristEinheit === 'Tag(e)') {
                setKuendigungsfristEinheit(vorlage.kuendigungsfristEinheit);
            }
        }
    };

    const handleSave = (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!titelId || !partnerId || !inhalt) {
            setError("Bitte füllen Sie die Pflichtfelder (Vorlage, Partner, Inhalt) aus.");
            return;
        }

        if (!individuellerTitel || !individuellerTitel.trim()) {
            setError("Bitte wählen Sie einen Vereinbarungstitel aus oder geben Sie einen individuellen Titel ein.");
            return;
        }

        setSubmitting(true);
        
        // === ANFANG: DATENOBJEKT MIT NEUEN FELDERN ERWEITERT ===
        const data = {
            titel_id: titelId,
            individueller_titel: individuellerTitel || null,
            empfaenger_user_id: partnerId,
            inhalt: inhalt,
            bearbeiter_von: bearbeiterVon,
            bearbeiter_an: bearbeiterAn,
            kurze_zusammenfassung: zusammenfassung,
            anlagen: anlagen,
            gueltigkeit_von: gueltigkeitVon || null,
            gueltigkeit_bis: gueltigkeitBis || null,
            kuendigungsfrist_wert: kuendigungsfristWert || null,
            kuendigungsfrist_einheit: kuendigungsfristWert ? kuendigungsfristEinheit : null,
            // unterzeichnungsdatum wird serverseitig oder bei Aktion gesetzt
        };
        // === ENDE: DATENOBJEKT MIT NEUEN FELDERN ERWEITERT ===

        VereinbarungService.createAgreement(data)
            .then(() => {
                setSuccess("Vereinbarung erfolgreich erstellt! Sie werden weitergeleitet...");
                setTimeout(() => {
                    navigate('/agreements');
                }, 2000);
            })
            .catch(err => {
                const errorMessage = (err.response?.data?.message) || "Ein unbekannter Fehler ist aufgetreten.";
                setError(errorMessage);
                setSubmitting(false);
            });
    };

    // Moderne Styles
    const styles = {
        pageContainer: {
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            minHeight: '100vh',
            paddingTop: '40px',
            paddingBottom: '60px'
        },
        mainContainer: {
            maxWidth: '1200px',
            margin: '0 auto'
        },
        header: {
            color: '#ffffff',
            fontSize: '42px',
            fontWeight: '700',
            marginBottom: '40px',
            textAlign: 'center',
            textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
            letterSpacing: '-0.5px'
        },
        mainCard: {
            borderRadius: '25px',
            border: 'none',
            boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
            overflow: 'hidden',
            background: '#ffffff'
        },
        cardHeader: {
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '25px 35px',
            borderBottom: 'none'
        },
        cardBody: {
            padding: '40px 35px',
            background: '#ffffff'
        },
        sectionTitle: {
            fontSize: '18px',
            fontWeight: '600',
            color: '#667eea',
            marginBottom: '25px',
            marginTop: '35px',
            paddingBottom: '10px',
            borderBottom: '2px solid #f0f2ff',
            display: 'flex',
            alignItems: 'center'
        },
        sectionIcon: {
            marginRight: '10px',
            fontSize: '20px'
        },
        formGroup: {
            marginBottom: '25px'
        },
        formLabel: {
            fontWeight: '500',
            color: '#4a5568',
            marginBottom: '8px',
            fontSize: '14px',
            letterSpacing: '0.3px'
        },
        requiredStar: {
            color: '#e53e3e',
            marginLeft: '4px'
        },
        formControl: {
            borderRadius: '12px',
            border: '2px solid #e2e8f0',
            padding: '12px 16px',
            fontSize: '15px',
            transition: 'all 0.3s ease',
            backgroundColor: '#f7fafc',
            ':hover': {
                borderColor: '#cbd5e0'
            },
            ':focus': {
                borderColor: '#667eea',
                backgroundColor: '#ffffff',
                boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
            }
        },
        formSelect: {
            borderRadius: '12px',
            border: '2px solid #e2e8f0',
            padding: '12px 16px',
            fontSize: '15px',
            transition: 'all 0.3s ease',
            backgroundColor: '#f7fafc',
            cursor: 'pointer'
        },
        textArea: {
            borderRadius: '12px',
            border: '2px solid #e2e8f0',
            padding: '12px 16px',
            fontSize: '15px',
            transition: 'all 0.3s ease',
            backgroundColor: '#f7fafc',
            resize: 'vertical',
            minHeight: '100px'
        },
        placeholderButton: {
            background: 'linear-gradient(135deg, #ffb347 0%, #ffcc33 100%)',
            border: 'none',
            borderRadius: '10px',
            padding: '8px 20px',
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: '15px',
            boxShadow: '0 4px 15px rgba(255, 179, 71, 0.3)',
            transition: 'all 0.3s ease'
        },
        quillContainer: {
            borderRadius: '12px',
            overflow: 'hidden',
            border: '2px solid #e2e8f0',
            backgroundColor: '#f7fafc'
        },
        submitButton: {
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            border: 'none',
            borderRadius: '12px',
            padding: '14px 40px',
            fontSize: '16px',
            fontWeight: '600',
            letterSpacing: '0.5px',
            boxShadow: '0 10px 30px rgba(102, 126, 234, 0.3)',
            transition: 'all 0.3s ease',
            marginTop: '30px'
        },
        alert: {
            borderRadius: '12px',
            padding: '16px 20px',
            marginTop: '20px',
            marginBottom: '20px',
            border: 'none',
            fontSize: '15px'
        },
        successAlert: {
            background: 'linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)',
            color: '#0c5460'
        },
        errorAlert: {
            background: 'linear-gradient(135deg, #fccb90 0%, #ff9a9e 100%)',
            color: '#721c24'
        },
        loadingContainer: {
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        },
        loadingSpinner: {
            width: '60px',
            height: '60px',
            borderWidth: '4px',
            borderColor: '#ffffff',
            borderTopColor: 'transparent'
        },
        loadingText: {
            color: '#ffffff',
            fontSize: '18px',
            marginTop: '20px',
            fontWeight: '500'
        },
        infoBox: {
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            borderRadius: '15px',
            padding: '20px',
            marginBottom: '30px',
            color: '#ffffff'
        },
        infoText: {
            margin: '0',
            fontSize: '14px',
            lineHeight: '1.6'
        }
    };

    if (loading) {
        return (
            <div style={styles.loadingContainer}>
                <Spinner animation="border" style={styles.loadingSpinner} />
                <p style={styles.loadingText}>Lade Formulardaten...</p>
            </div>
        );
    }

    const selectedTemplate = titelList.find(t => String(t.titel_id) === String(titelId));
    const selectedTemplateName = selectedTemplate ? selectedTemplate.titel : '';

    return (
        <div style={styles.pageContainer}>
            <Container style={styles.mainContainer}>
                <h1 style={styles.header}>✨ Neue Vereinbarung erstellen</h1>
                
                <Card style={styles.mainCard}>
                    <div style={styles.cardHeader}>
                        <div style={styles.infoBox}>
                            <p style={styles.infoText}>
                                📋 Willkommen im Vereinbarungs-Assistenten! Füllen Sie die nachfolgenden Felder aus, um eine neue Vereinbarung zu erstellen.
                                Pflichtfelder sind mit einem * gekennzeichnet.
                            </p>
                        </div>
                    </div>
                    
                    <Card.Body style={styles.cardBody}>
                        <Form onSubmit={handleSave}>
                            {/* === ANFANG: FORMULAR MIT NEUEN FELDERN ERWEITERT === */}
                            
                            {/* Hauptinformationen */}
                            <div style={styles.sectionTitle}>
                                <span style={styles.sectionIcon}>📝</span>
                                Hauptinformationen
                            </div>
                            
                            <Row>
                                <Col md={6}>
                                    <Form.Group style={styles.formGroup} controlId="formTitelAuswahl">
                                        <Form.Label style={styles.formLabel}>
                                            Vereinbarungstitel
                                            <span style={styles.requiredStar}>*</span>
                                        </Form.Label>
                                        <Form.Select
                                            style={styles.formSelect}
                                            value={individuellerTitel}
                                            onChange={e => setIndividuellerTitel(e.target.value)}
                                        >
                                            {selectedTemplateName && (
                                                <option value={selectedTemplateName}>
                                                    {selectedTemplateName} (System-Vorgabe)
                                                </option>
                                            )}
                                            {titelKatalogList
                                                .filter(k => k.bezeichnung !== selectedTemplateName)
                                                .map(k => (
                                                    <option key={k.katalog_id} value={k.bezeichnung}>{k.bezeichnung}</option>
                                                ))}
                                        </Form.Select>
                                        <Form.Text className="text-muted">
                                            Vom System vorgegebener Titel. Optional einen eigenen Titel auswählen –
                                            gilt nur für diese Vereinbarung und ändert die Systemvorlage nicht.
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group style={styles.formGroup} controlId="formPartner">
                                        <Form.Label style={styles.formLabel}>
                                            Vertragspartner
                                            <span style={styles.requiredStar}>*</span>
                                        </Form.Label>
                                        <Form.Select 
                                            style={styles.formSelect}
                                            value={partnerId} 
                                            onChange={e => setPartnerId(e.target.value)} 
                                            required
                                        >
                                            <option value="">Bitte Partner auswählen</option>
                                            {/* Vereinheitlichte, stufenabhaengige Partnerliste
                                                ({ key, userId, label }); Wert = verknuepfte users.id */}
                                            {partnerList.map((partner) => (
                                                <option key={partner.key} value={partner.userId}>
                                                    {partner.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>

                            {/* Bearbeiter */}
                            <div style={styles.sectionTitle}>
                                <span style={styles.sectionIcon}>👥</span>
                                Bearbeiter
                            </div>
                            
                            <Row>
                                <Col md={6}>
                                    <Form.Group style={styles.formGroup} controlId="formBearbeiterVon">
                                        <Form.Label style={styles.formLabel}>Bearbeiter (Von)</Form.Label>
                                        <Form.Control 
                                            style={styles.formControl}
                                            type="text" 
                                            value={bearbeiterVon} 
                                            onChange={e => setBearbeiterVon(e.target.value)} 
                                            placeholder="Name eingeben..."
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group style={styles.formGroup} controlId="formBearbeiterAn">
                                        <Form.Label style={styles.formLabel}>Bearbeiter (An)</Form.Label>
                                        <Form.Control 
                                            style={styles.formControl}
                                            type="text" 
                                            value={bearbeiterAn} 
                                            onChange={e => setBearbeiterAn(e.target.value)} 
                                            placeholder="Name eingeben..."
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            
                            {/* Zeitraum */}
                            <div style={styles.sectionTitle}>
                                <span style={styles.sectionIcon}>📅</span>
                                Gültigkeitszeitraum
                            </div>
                            
                            <Row>
                                <Col md={6}>
                                    <Form.Group style={styles.formGroup} controlId="formGueltigkeitVon">
                                        <Form.Label style={styles.formLabel}>Gültig von</Form.Label>
                                        <Form.Control 
                                            style={styles.formControl}
                                            type="date" 
                                            value={gueltigkeitVon} 
                                            onChange={e => setGueltigkeitVon(e.target.value)} 
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group style={styles.formGroup} controlId="formGueltigkeitBis">
                                        <Form.Label style={styles.formLabel}>Gültig bis</Form.Label>
                                        <Form.Control 
                                            style={styles.formControl}
                                            type="date" 
                                            value={gueltigkeitBis} 
                                            onChange={e => setGueltigkeitBis(e.target.value)} 
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            
                            {/* Inhalt */}
                            <div style={styles.sectionTitle}>
                                <span style={styles.sectionIcon}>📄</span>
                                Vereinbarungsinhalt
                            </div>
                            
                            <Form.Group style={styles.formGroup} controlId="formZusammenfassung">
                                <Form.Label style={styles.formLabel}>Kurze Zusammenfassung</Form.Label>
                                <Form.Control 
                                    as="textarea" 
                                    rows={2} 
                                    style={styles.textArea}
                                    value={zusammenfassung} 
                                    onChange={e => setZusammenfassung(e.target.value)} 
                                    placeholder="Geben Sie eine kurze Zusammenfassung ein..."
                                />
                            </Form.Group>

                            <Form.Group style={styles.formGroup} controlId="formInhalt">
                                <Form.Label style={styles.formLabel}>
                                    Inhalt der Vereinbarung
                                    <span style={styles.requiredStar}>*</span>
                                </Form.Label>
                                <Button 
                                    variant="warning"
                                    size="sm" 
                                    style={styles.placeholderButton}
                                    onClick={() => setModalShow(true)}
                                    onMouseEnter={(e) => {
                                        e.target.style.transform = 'translateY(-2px)';
                                        e.target.style.boxShadow = '0 6px 20px rgba(255, 179, 71, 0.4)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.transform = 'translateY(0)';
                                        e.target.style.boxShadow = '0 4px 15px rgba(255, 179, 71, 0.3)';
                                    }}
                                >
                                    🏷️ Platzhalter einfügen
                                </Button>
                                <div style={styles.quillContainer}>
                                    <ReactQuill 
                                        ref={quillRef} 
                                        theme="snow" 
                                        value={inhalt} 
                                        onChange={setInhalt} 
                                        className="resizable-editor" 
                                        style={{ minHeight: '300px' }} 
                                    />
                                </div>
                            </Form.Group>

                            {/* Zusätzliche Informationen */}
                            <div style={styles.sectionTitle}>
                                <span style={styles.sectionIcon}>📎</span>
                                Zusätzliche Informationen
                            </div>

                            <Form.Group style={styles.formGroup} controlId="formAnlagen">
                                <Form.Label style={styles.formLabel}>Anlagen</Form.Label>
                                <Form.Control 
                                    as="textarea" 
                                    rows={3} 
                                    style={styles.textArea}
                                    value={anlagen} 
                                    onChange={e => setAnlagen(e.target.value)} 
                                    placeholder="Eine Anlage pro Zeile..."
                                />
                            </Form.Group>

                            <Form.Group as={Row} style={styles.formGroup} controlId="formKuendigungsfrist">
                                <Form.Label column sm={2} style={styles.formLabel}>
                                    ⏰ Kündigungsfrist
                                </Form.Label>
                                <Col sm={5}>
                                    <Form.Control 
                                        style={styles.formControl}
                                        type="number" 
                                        value={kuendigungsfristWert} 
                                        onChange={e => setKuendigungsfristWert(e.target.value)} 
                                        placeholder="z.B. 3"
                                    />
                                </Col>
                                <Col sm={5}>
                                    <Form.Select 
                                        style={styles.formSelect}
                                        value={kuendigungsfristEinheit} 
                                        onChange={e => setKuendigungsfristEinheit(e.target.value)}
                                    >
                                        <option>Tag(e)</option>
                                        <option>Woche(n)</option>
                                        <option>Monat(e)</option>
                                        <option>Jahre</option>
                                    </Form.Select>
                                </Col>
                            </Form.Group>
                            {/* === ENDE: FORMULAR MIT NEUEN FELDERN ERWEITERT === */}

                            {error && (
                                <Alert style={{...styles.alert, ...styles.errorAlert}}>
                                    ⚠️ {error}
                                </Alert>
                            )}
                            {success && (
                                <Alert style={{...styles.alert, ...styles.successAlert}}>
                                    ✅ {success}
                                </Alert>
                            )}

                            <div style={{ textAlign: 'center' }}>
                                <Button 
                                    variant="primary" 
                                    type="submit" 
                                    disabled={submitting}
                                    style={styles.submitButton}
                                    onMouseEnter={(e) => {
                                        if (!submitting) {
                                            e.target.style.transform = 'translateY(-2px)';
                                            e.target.style.boxShadow = '0 15px 40px rgba(102, 126, 234, 0.4)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.transform = 'translateY(0)';
                                        e.target.style.boxShadow = '0 10px 30px rgba(102, 126, 234, 0.3)';
                                    }}
                                >
                                    {submitting ? (
                                        <>
                                            <Spinner 
                                                as="span" 
                                                animation="border" 
                                                size="sm" 
                                                role="status" 
                                                aria-hidden="true" 
                                                style={{ marginRight: '10px' }}
                                            />
                                            Speichert...
                                        </>
                                    ) : (
                                        <>💾 Vereinbarung speichern</>
                                    )}
                                </Button>
                            </div>
                        </Form>
                    </Card.Body>
                </Card>
            </Container>

            <PlatzhalterAuswahlModal 
                show={modalShow}
                onHide={() => setModalShow(false)}
                onSelectPlaceholder={handleSelectPlaceholder}
            />
        </div>
    );
};

export default VereinbarungErstellen;